import React, { useRef, useState } from 'react';
import styles from './index.module.scss';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { customClickReportSimple } from '@/dataPort';
import { addSearchHistory, isSkuId } from '@/api/newApi/search';
import { useHistory } from 'react-router-dom';
import { getHomePageImageUrl } from '@/util/getImg';
import { buildSearchListHistoryLocation } from '@/pages/SearchList/utils/searchListUrl';

// React 17 须用小写 fetchpriority，否则控制台 warning 且属性无法落到 DOM
const lcpHighPriorityProps = {
  fetchpriority: 'high',
} as React.ImgHTMLAttributes<HTMLImageElement>;

interface BannerItem {
  jumpType: 'url' | 'category' | 'keyword';
  image: string;
  displayOrder: number;
  id: string;
  jumpUrl?: string;
  categoryId?: string;
  keyword?: string;
  status?: number;
}

interface BigBannerConfig {
  id: string;
  banners: BannerItem[];
  status: number;
}

interface ZoneBannerConfig {
  layoutType: 'one-row-two' | 'one-row-three' | 'one-row-five';
  id: string;
  banners: BannerItem[];
}

interface BannerData {
  moduleType: string;
  bigBannerConfig?: BigBannerConfig;
  zoneBannerConfig?: ZoneBannerConfig;
  moduleId: number;
  priority: number;
}

interface BannerProps {
  data: BannerData;
  type: 'big' | 'zone';
}

interface ArrowProps {
  className?: string;
  onClick?: () => void;
  visible?: boolean;
  direction: 'next' | 'prev';
}

const AUTOPLAY_SPEED = 5000;

const BannerArrow: React.FC<ArrowProps> = ({ className, onClick, visible, direction }) => (
  <button
    className={`${className || ''} ${styles.bannerArrow} ${visible ? styles.bannerArrowVisible : ''} ${
      direction === 'prev' ? styles.bannerArrowPrev : styles.bannerArrowNext
    }`.trim()}
    onClick={onClick}
    type="button"
    aria-label={direction === 'prev' ? 'Previous banner' : 'Next banner'}
  >
    <span className={styles.bannerArrowInner}>
      <i className={`iconFont icon-right ${styles.bannerArrowIcon}`}></i>
    </span>
  </button>
);

const useBannerClickHandler = () => {
  const history = useHistory();

  const handleBannerClick = async (banner: BannerItem) => {
    if (banner.jumpType === 'url' && banner.jumpUrl) {
      customClickReportSimple('HomePage_BannerUrlClk', banner);
      window._openPage(banner.jumpUrl, '_blank');
    } else if (banner.jumpType === 'category' && banner.categoryId) {
      customClickReportSimple('HomePage_BannerCategoryClk', banner);
      await handleCategoryClick(banner.categoryId);
    } else if (banner.jumpType === 'keyword' && banner.keyword) {
      customClickReportSimple('HomePage_BannerKeywordClk', banner);
      await handleKeywordClick(banner.keyword);
    }
  };

  const handleCategoryClick = async (categoryId: string) => {
    const categoryItem = {
      catName: categoryId,
      catId: parseInt(categoryId, 10),
    };

    // 写搜索历史不属于关键路径，异步落库避免阻塞进入搜列页。
    void addSearchHistory(categoryItem.catName);
    const result = (await isSkuId(categoryItem.catName)) as any;
    if (result.success && result.value) {
      window.location.href = `${window.location.origin}/sku/${result.value}`;
      return;
    }

    history.push(
      buildSearchListHistoryLocation(
        {
          cat: categoryItem.catId,
          pageNo: 1,
          sort: 0,
        },
        {
          cataIdList: `${categoryItem.catId}`,
          cat: categoryItem.catId,
        },
        {
          query: categoryItem.catName || '',
          searchTriggerTs: Date.now(),
        },
      ),
    );
  };

  const handleKeywordClick = async (keyword: string) => {
    // 写搜索历史不属于关键路径，异步落库避免阻塞进入搜列页。
    void addSearchHistory(keyword);
    const result = (await isSkuId(keyword)) as any;
    if (result.success && result.value) {
      window.location.href = `${window.location.origin}/sku/${result.value}`;
      return;
    }

    history.replace(
      buildSearchListHistoryLocation(
        {
          key: keyword,
          pageNo: 1,
          sort: 0,
        },
        {},
        {
          query: keyword,
          searchTriggerTs: Date.now(),
        },
      ),
    );
  };

  return { handleBannerClick };
};

const Banner: React.FC<BannerProps> = ({ data, type }) => {
  const { handleBannerClick } = useBannerClickHandler();
  const sliderRef = useRef<Slider | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleBigBannerMouseEnter = () => {
    setIsHovered(true);
    sliderRef.current?.slickPause();
  };

  const handleBigBannerMouseLeave = () => {
    setIsHovered(false);
    sliderRef.current?.slickPlay();
  };

  const renderBigBanner = () => {
    if (!data.bigBannerConfig?.banners?.length) return null;

    const banners = [...data.bigBannerConfig.banners]
      .filter(banner => Boolean(banner?.image?.trim()))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (!banners.length) return null;

    if (banners.length === 1) {
      return (
        <div className={styles.bigBannerContainer}>
          <img
            className={styles.bigBannerImage}
            src={getHomePageImageUrl(banners[0].image)}
            alt="banner"
            loading="eager"
            onClick={() => handleBannerClick(banners[0])}
            {...lcpHighPriorityProps}
          />
        </div>
      );
    }

    const settings = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: AUTOPLAY_SPEED,
      pauseOnHover: false,
      arrows: true,
      beforeChange: (_current: number, next: number) => {
        setActiveIndex(next);
      },
      appendDots: () => (
        <div className={styles.bannerDotsWrap}>
          <ul className={styles.bannerDotsList}>
            {banners.map((banner, index) => {
              const isActive = index === activeIndex;

              return (
                <li
                  key={banner.id}
                  className={`${styles.bannerDot} ${isActive ? styles.bannerDotActive : ''}`}
                  onClick={() => sliderRef.current?.slickGoTo(index)}
                >
                  {isActive && (
                    <span
                      key={`${banner.id}-${activeIndex}`}
                      className={`${styles.bannerDotProgress} ${isHovered ? styles.bannerDotProgressPaused : ''}`}
                    ></span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ),
      customPaging: () => <button type="button" className={styles.bannerDotButton} aria-hidden="true"></button>,
      nextArrow: <BannerArrow direction="next" visible={isHovered} />,
      prevArrow: <BannerArrow direction="prev" visible={isHovered} />,
    };

    return (
      <div
        className={styles.bigBannerContainer}
        onMouseEnter={handleBigBannerMouseEnter}
        onMouseLeave={handleBigBannerMouseLeave}
      >
        <Slider ref={sliderRef} {...settings}>
          {banners.map((banner, index) => (
            <div key={banner.id} className={styles.bigBannerSlide}>
              <img
                className={styles.bigBannerImage}
                src={getHomePageImageUrl(banner.image)}
                alt="banner"
                loading={index === 0 ? 'eager' : 'lazy'}
                onClick={() => handleBannerClick(banner)}
                {...(index === 0 ? lcpHighPriorityProps : {})}
              />
            </div>
          ))}
        </Slider>
      </div>
    );
  };

  const renderZoneBanner = () => {
    if (!data.zoneBannerConfig?.banners?.length) return null;

    const { layoutType, banners } = data.zoneBannerConfig;
    const sortedBanners = [...banners]
      .filter(banner => Boolean(banner?.image?.trim()))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (!sortedBanners.length) return null;

    const getLayoutClass = () => {
      switch (layoutType) {
        case 'one-row-two':
          return styles.zoneBannerTwo;
        case 'one-row-three':
          return styles.zoneBannerThree;
        case 'one-row-five':
          return styles.zoneBannerFive;
        default:
          return styles.zoneBannerTwo;
      }
    };

    return (
      <div className={`${styles.zoneBannerContainer} ${getLayoutClass()}`}>
        {sortedBanners.map(banner => (
          <div key={banner.id} className={styles.zoneBannerItem}>
            <img
              className={styles.zoneBannerImage}
              src={getHomePageImageUrl(banner.image)}
              alt="banner"
              loading="lazy"
              onClick={() => handleBannerClick(banner)}
            />
          </div>
        ))}
      </div>
    );
  };

  return <div className={styles.bannerWrapper}>{type === 'big' ? renderBigBanner() : renderZoneBanner()}</div>;
};

export default Banner;
